import { useState } from 'react';
import { Check, Building2, Users } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MultiSelect, type MultiSelectGroup } from '@/components/ui/multi-select';
import { cn } from '@/lib/utils';
import { useAccountUsers } from '@/hooks/use-account-users';
import { useCreatePersona } from '@/hooks/use-personas';
import { toast } from '@/components/ui/sonner';
import { PERSONA_STATUS_CONFIG } from '../constants';
import type { Persona, PersonaValidationStatus } from '@/types';

interface CreatePersonaModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onFailed: (message: string) => void;
	onCreating: () => void;
	brandPersona?: Persona | null;
	agentPersonas?: Persona[];
}

type PersonaScope = 'brand' | 'agent';

const BADGE_CLASS_NAME: Record<PersonaValidationStatus, string> = {
	validated: 'bg-green-bg text-green-foreground',
	validation_pending: 'bg-yellow-bg text-yellow-foreground'
};

export function CreatePersonaModal({
	open,
	onOpenChange,
	onFailed,
	onCreating,
	brandPersona,
	agentPersonas = []
}: CreatePersonaModalProps) {
	const brandAlreadyConfigured = brandPersona != null;
	const brandStatusConfig = brandPersona ? PERSONA_STATUS_CONFIG[brandPersona.validationStatus] : null;
	const [scope, setScope] = useState<PersonaScope>(brandAlreadyConfigured ? 'agent' : 'brand');
	const [agentUserIds, setAgentUserIds] = useState<string[]>([]);
	const { data: accountUsers } = useAccountUsers();
	const createPersona = useCreatePersona();

	const personaByUserId = new Map(
		agentPersonas.filter((persona) => persona.userId != null).map((persona) => [persona.userId as number, persona])
	);

	const agents = (accountUsers ?? []).filter((user) => user.role === 'agent');
	const configuredAgents = agents.filter((user) => personaByUserId.has(user.id));
	const unconfiguredAgents = agents.filter((user) => !personaByUserId.has(user.id));

	const agentOptions: MultiSelectGroup[] = [
		...(configuredAgents.length > 0
			? [
					{
						heading: 'Configured personas',
						options: configuredAgents.map((user) => {
							const persona = personaByUserId.get(user.id)!;
							const statusConfig = PERSONA_STATUS_CONFIG[persona.validationStatus];
							return {
								label: user.name,
								value: String(user.id),
								disabled: true,
								badge: { label: statusConfig.label, className: BADGE_CLASS_NAME[persona.validationStatus] }
							};
						})
					}
				]
			: []),
		...(unconfiguredAgents.length > 0
			? [{ heading: 'Agents', options: unconfiguredAgents.map((user) => ({ label: user.name, value: String(user.id) })) }]
			: [])
	];

	const handleCancel = () => {
		onOpenChange(false);
		setScope(brandAlreadyConfigured ? 'agent' : 'brand');
		setAgentUserIds([]);
	};

	const handleCreate = async () => {
		onOpenChange(false);
		onCreating();

		try {
			const result = await createPersona.mutateAsync(
				scope === 'brand' ? { scope: 'brand' } : { scope: 'agent', agentUserIds: agentUserIds.map(Number) }
			);

			if (result.jobId) return;

			// scope === 'agent' returns one result per agent instead of a single jobId.
			const results = result.results ?? [];
			const failures = results.filter((r) => r.error);
			if (failures.length > 0) {
				onFailed(failures[0]!.error ?? "We were unable to generate the persona as there weren't enough tickets.");
			}
		} catch (error) {
			const message =
				error instanceof Error ? error.message : "We were unable to generate the persona as there weren't enough tickets.";
			onFailed(message);
		} finally {
			setScope(brandAlreadyConfigured ? 'agent' : 'brand');
			setAgentUserIds([]);
		}
	};

	return (
		<Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(next) : handleCancel())}>
			<DialogContent
				showCloseButton={false}
				className='max-w-[600px] gap-0 rounded-[20px] border-gray-500 bg-white/80 p-0 shadow-[0px_4px_12px_0px_rgba(0,0,0,0.1)] backdrop-blur-[6px]!'
			>
				<DialogHeader className='h-[50px] flex-row items-center justify-start px-3 py-2'>
					<DialogTitle className='text-sm font-semibold text-ink'>Create Persona</DialogTitle>
				</DialogHeader>
				<Separator />

				<div className='flex flex-col gap-6 p-4'>
					<div className='flex flex-col gap-3'>
						<p className='text-xs font-medium text-gray-900'>Who would you like to create persona for ?</p>

						<button
							type='button'
							disabled={brandAlreadyConfigured}
							onClick={() => setScope('brand')}
							className={cn(
								'flex flex-col gap-3 rounded-2xl border p-3 text-left transition-colors',
								brandAlreadyConfigured
									? 'border-2 border-gray-100 bg-gray-300 cursor-not-allowed'
									: scope === 'brand'
										? 'border-2 border-border bg-white'
										: 'border-border/60 hover:bg-muted/50'
							)}
						>
							<div className='flex items-center justify-between'>
								<Building2 className='size-5 shrink-0' />
								{brandStatusConfig ? (
									<Badge variant={brandStatusConfig.badgeVariant}>{brandStatusConfig.label}</Badge>
								) : (
									scope === 'brand' && <Check className='size-4 shrink-0' />
								)}
							</div>
							<div>
								<p className='text-[13px] font-medium text-ink'>Brand Persona</p>
								<p className='text-[11px] text-gray-900'>
									{brandAlreadyConfigured
										? 'Already created. Use Regenerate on the brand persona to update it.'
										: 'A unified brand Persona to maintain consistency across all platforms.'}
								</p>
							</div>
						</button>

						<button
							type='button'
							onClick={() => setScope('agent')}
							className={cn(
								'flex flex-col gap-3 rounded-2xl border p-3 text-left transition-colors',
								scope === 'agent' ? 'border-2 border-border bg-white' : 'border-border/60 hover:bg-muted/50'
							)}
						>
							<div className='flex items-center justify-between'>
								<Users className='size-5 shrink-0' />
								{scope === 'agent' && <Check className='size-4 shrink-0' />}
							</div>
							<div>
								<p className='text-[13px] font-medium text-ink'>Agents Persona</p>
								<p className='text-[11px] text-gray-800'>Behaviour will be created for the selected agents</p>
							</div>
						</button>
					</div>

					{scope === 'agent' && (
						<div className='flex flex-col gap-1.5'>
							<p className='text-xs font-medium text-gray-900'>Choose Agents</p>
							<MultiSelect
								options={agentOptions}
								defaultValue={agentUserIds}
								onValueChange={setAgentUserIds}
								placeholder='Choose Agents'
								hideSelectAll
							/>
							<p className='text-[11px] text-gray-800'>Persona will be created for the selected agents</p>
						</div>
					)}
				</div>

				<Separator />
				<DialogFooter className='p-3'>
					<Button variant='outline' className='rounded-full font-normal' onClick={handleCancel}>
						Cancel
					</Button>
					<Button
						className='rounded-full font-normal'
						disabled={
							(scope === 'agent' && agentUserIds.length === 0) ||
							(scope === 'brand' && brandAlreadyConfigured) ||
							createPersona.isPending
						}
						onClick={() => {
							if (scope === 'agent' && agentUserIds.length === 0) {
								toast.error('Choose at least one agent');
								return;
							}
							void handleCreate();
						}}
					>
						Create Persona
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
